import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { Popover } from 'antd';

import { FixedPricePerUnits } from 'entities/tariff/ui/FixedPricePerUnits/FixedPricePerUnits';
import {
	ButtonStyled,
	HiddenPriceContainerStyled,
	PeriodStyled,
	PriceStyled,
} from 'entities/tariff/ui/PlanPrice/PlanPrice.styles';
import { useAuthorization } from 'entities/user/hooks/useAuthorization';
import { TariffOverride } from '../../api/contracts';
import { PricePerUnit } from '../PricePerUnits/PricePerUnits';

interface PlanPriceProps {
	price: number | undefined;
	canPurchaseApps: boolean | undefined;
	isFree: boolean;
	overrides: TariffOverride[] | undefined;
	hasAdditionalFeatures: boolean;
}

export const PlanPrice: FC<PlanPriceProps> = ({ price, canPurchaseApps, isFree, hasAdditionalFeatures, overrides }) => {
	const { t } = useTranslation();
	const authorization = useAuthorization();
	const internalAccount = authorization?.blockAppInstallation;

	const priceOverride = overrides?.find(n => n.type === 2);

	if (!canPurchaseApps) {
		return (
			<HiddenPriceContainerStyled>
				{isFree ? (
					<PriceStyled>{t(`Marketplace_Free`)}</PriceStyled>
				) : (
					<PriceStyled>{t(`Marketplace_Paid`)}</PriceStyled>
				)}
				<Popover
					overlayInnerStyle={{ width: `231px` }}
					placement='leftTop'
					content={
						<span>
							{internalAccount ? t(`Marketplace_Popover_Price_Internal`) : t(`Marketplace_Popover_Price`)}
						</span>
					}
				>
					<QuestionCircleOutlined />
				</Popover>
			</HiddenPriceContainerStyled>
		);
	}
	if (!overrides) {
		return null;
	}

	const content = priceOverride ? (
		<FixedPricePerUnits override={overrides.filter(n => n.type === 2)[0]} pricePerUnit={price ?? 0} />
	) : (
		<PricePerUnit overrides={overrides.filter(n => n.type === 0)} />
	);

	return (
		<div>
			{isFree && <PriceStyled>{t(`Marketplace_Free`)}</PriceStyled>}
			{!isFree && price && (
				<div
					style={{
						display: `flex`,
						flexDirection: `column`,
						alignItems: `flex-start`,
						justifyContent: `flex-start`,
					}}
				>
					<PriceStyled>
						{`${hasAdditionalFeatures ? `от` : ``} ${
							priceOverride?.price.toLocaleString() ?? price.toLocaleString()
						}`}
						&thinsp;₽&thinsp;/&thinsp;{t(`Marketplace_Per_Month`)}
					</PriceStyled>
					<PeriodStyled>
						{priceOverride
							? t(`Marketplace_Price_Per_Multiple_Units`, {
									start: 1,
									end: priceOverride.toNumberOfUnits ?? ``,
							  })
							: t(`Marketplace_Per_One_Unit`)}
					</PeriodStyled>
					{overrides.length !== 0 && (
						<Popover content={content} placement='right'>
							<ButtonStyled type='link'>
								{t(`Marketplace_Price_For_Number_Of_Establishments`)}
							</ButtonStyled>
						</Popover>
					)}
				</div>
			)}
		</div>
	);
};
